/**
 * The country list every `select` field named `country` offers.
 *
 * A table of its own rather than 250 strings inside a page's `fields` array:
 * three forms ask the question today (the home page's contact block in English
 * and in Turkish, and the Contact Us page) and inlining the list would be three
 * copies of it to keep in step. It is content, so it lives under `content/`.
 *
 * **Written out in sorted order, not sorted at runtime.** The home page hydrates,
 * and `localeCompare` can order two strings differently in the build's ICU and in
 * the reader's browser — which is a hydration mismatch across 250 options. A
 * literal in the right order cannot disagree with itself. `COUNTRIES_TR` is in
 * *Turkish* alphabetical order (ç after c, ğ after g, ı before i, ö after o,
 * ş after s, ü after u), which is why Çad sits after Curaçao, Kıbrıs before
 * Kiribati, Irak before İran and Özbekistan between Orta Afrika and Pakistan.
 * An English sort would scatter all four, and a Turkish reader scanning for
 * Türkiye would not find it where they looked.
 *
 * ISO 3166-1, common English names in `COUNTRIES` (so Türkiye, Czechia,
 * Eswatini — the current names, not the ones they replaced), plus Kosovo, which
 * is not in ISO but is on every list a business contact form uses. The two
 * arrays are the same 250 places in the same two languages; add to one and add
 * to the other.
 *
 * When the HubSpot embed finally replaces these fields (see `pageForms.ts`) its
 * own list arrives with it and this file can go.
 */
export const COUNTRIES = [
  'Afghanistan',
  'Åland Islands',
  'Albania',
  'Algeria',
  'American Samoa',
  'Andorra',
  'Angola',
  'Anguilla',
  'Antarctica',
  'Antigua and Barbuda',
  'Argentina',
  'Armenia',
  'Aruba',
  'Australia',
  'Austria',
  'Azerbaijan',
  'Bahamas',
  'Bahrain',
  'Bangladesh',
  'Barbados',
  'Belarus',
  'Belgium',
  'Belize',
  'Benin',
  'Bermuda',
  'Bhutan',
  'Bolivia',
  'Bonaire, Sint Eustatius and Saba',
  'Bosnia and Herzegovina',
  'Botswana',
  'Bouvet Island',
  'Brazil',
  'British Indian Ocean Territory',
  'Brunei',
  'Bulgaria',
  'Burkina Faso',
  'Burundi',
  'Cabo Verde',
  'Cambodia',
  'Cameroon',
  'Canada',
  'Cayman Islands',
  'Central African Republic',
  'Chad',
  'Chile',
  'China',
  'Christmas Island',
  'Cocos (Keeling) Islands',
  'Colombia',
  'Comoros',
  'Congo',
  'Congo (Democratic Republic)',
  'Cook Islands',
  'Costa Rica',
  "Côte d'Ivoire",
  'Croatia',
  'Cuba',
  'Curaçao',
  'Cyprus',
  'Czechia',
  'Denmark',
  'Djibouti',
  'Dominica',
  'Dominican Republic',
  'Ecuador',
  'Egypt',
  'El Salvador',
  'Equatorial Guinea',
  'Eritrea',
  'Estonia',
  'Eswatini',
  'Ethiopia',
  'Falkland Islands',
  'Faroe Islands',
  'Fiji',
  'Finland',
  'France',
  'French Guiana',
  'French Polynesia',
  'French Southern Territories',
  'Gabon',
  'Gambia',
  'Georgia',
  'Germany',
  'Ghana',
  'Gibraltar',
  'Greece',
  'Greenland',
  'Grenada',
  'Guadeloupe',
  'Guam',
  'Guatemala',
  'Guernsey',
  'Guinea',
  'Guinea-Bissau',
  'Guyana',
  'Haiti',
  'Heard Island and McDonald Islands',
  'Holy See',
  'Honduras',
  'Hong Kong',
  'Hungary',
  'Iceland',
  'India',
  'Indonesia',
  'Iran',
  'Iraq',
  'Ireland',
  'Isle of Man',
  'Israel',
  'Italy',
  'Jamaica',
  'Japan',
  'Jersey',
  'Jordan',
  'Kazakhstan',
  'Kenya',
  'Kiribati',
  'Kosovo',
  'Kuwait',
  'Kyrgyzstan',
  'Laos',
  'Latvia',
  'Lebanon',
  'Lesotho',
  'Liberia',
  'Libya',
  'Liechtenstein',
  'Lithuania',
  'Luxembourg',
  'Macao',
  'Madagascar',
  'Malawi',
  'Malaysia',
  'Maldives',
  'Mali',
  'Malta',
  'Marshall Islands',
  'Martinique',
  'Mauritania',
  'Mauritius',
  'Mayotte',
  'Mexico',
  'Micronesia',
  'Moldova',
  'Monaco',
  'Mongolia',
  'Montenegro',
  'Montserrat',
  'Morocco',
  'Mozambique',
  'Myanmar',
  'Namibia',
  'Nauru',
  'Nepal',
  'Netherlands',
  'New Caledonia',
  'New Zealand',
  'Nicaragua',
  'Niger',
  'Nigeria',
  'Niue',
  'Norfolk Island',
  'North Korea',
  'North Macedonia',
  'Northern Mariana Islands',
  'Norway',
  'Oman',
  'Pakistan',
  'Palau',
  'Palestine',
  'Panama',
  'Papua New Guinea',
  'Paraguay',
  'Peru',
  'Philippines',
  'Pitcairn Islands',
  'Poland',
  'Portugal',
  'Puerto Rico',
  'Qatar',
  'Réunion',
  'Romania',
  'Russia',
  'Rwanda',
  'Saint Barthélemy',
  'Saint Helena, Ascension and Tristan da Cunha',
  'Saint Kitts and Nevis',
  'Saint Lucia',
  'Saint Martin (French part)',
  'Saint Pierre and Miquelon',
  'Saint Vincent and the Grenadines',
  'Samoa',
  'San Marino',
  'Sao Tome and Principe',
  'Saudi Arabia',
  'Senegal',
  'Serbia',
  'Seychelles',
  'Sierra Leone',
  'Singapore',
  'Sint Maarten (Dutch part)',
  'Slovakia',
  'Slovenia',
  'Solomon Islands',
  'Somalia',
  'South Africa',
  'South Georgia and the South Sandwich Islands',
  'South Korea',
  'South Sudan',
  'Spain',
  'Sri Lanka',
  'Sudan',
  'Suriname',
  'Svalbard and Jan Mayen',
  'Sweden',
  'Switzerland',
  'Syria',
  'Taiwan',
  'Tajikistan',
  'Tanzania',
  'Thailand',
  'Timor-Leste',
  'Togo',
  'Tokelau',
  'Tonga',
  'Trinidad and Tobago',
  'Tunisia',
  'Türkiye',
  'Turkmenistan',
  'Turks and Caicos Islands',
  'Tuvalu',
  'Uganda',
  'Ukraine',
  'United Arab Emirates',
  'United Kingdom',
  'United States',
  'United States Minor Outlying Islands',
  'Uruguay',
  'Uzbekistan',
  'Vanuatu',
  'Venezuela',
  'Vietnam',
  'Virgin Islands (British)',
  'Virgin Islands (U.S.)',
  'Wallis and Futuna',
  'Western Sahara',
  'Yemen',
  'Zambia',
  'Zimbabwe',
]

/** The same 250 places in Turkish, in Turkish alphabetical order. */
export const COUNTRIES_TR = [
  'ABD Küçük Dış Adaları',
  'Afganistan',
  'Åland Adaları',
  'Almanya',
  'Amerika Birleşik Devletleri',
  'Amerikan Samoası',
  'Andorra',
  'Angola',
  'Anguilla',
  'Antarktika',
  'Antigua ve Barbuda',
  'Arjantin',
  'Arnavutluk',
  'Aruba',
  'Avustralya',
  'Avusturya',
  'Azerbaycan',
  'Bahamalar',
  'Bahreyn',
  'Bangladeş',
  'Barbados',
  'Batı Sahra',
  'Belarus',
  'Belçika',
  'Belize',
  'Benin',
  'Bermuda',
  'Birleşik Arap Emirlikleri',
  'Birleşik Krallık',
  'Bolivya',
  'Bonaire, Sint Eustatius ve Saba',
  'Bosna-Hersek',
  'Botsvana',
  'Bouvet Adası',
  'Brezilya',
  'Britanya Hint Okyanusu Toprakları',
  'Brunei',
  'Bulgaristan',
  'Burkina Faso',
  'Burundi',
  'Butan',
  'Cabo Verde',
  'Cayman Adaları',
  'Cebelitarık',
  'Cezayir',
  'Christmas Adası',
  'Cibuti',
  'Cocos (Keeling) Adaları',
  'Cook Adaları',
  'Curaçao',
  'Çad',
  'Çekya',
  'Çin',
  'Danimarka',
  'Dominik Cumhuriyeti',
  'Dominika',
  'Ekvador',
  'Ekvator Ginesi',
  'El Salvador',
  'Endonezya',
  'Eritre',
  'Ermenistan',
  'Estonya',
  'Esvatini',
  'Etiyopya',
  'Falkland Adaları',
  'Faroe Adaları',
  'Fas',
  'Fiji',
  'Fildişi Sahili',
  'Filipinler',
  'Filistin',
  'Finlandiya',
  'Fransa',
  'Fransız Guyanası',
  'Fransız Güney Toprakları',
  'Fransız Polinezyası',
  'Gabon',
  'Gambiya',
  'Gana',
  'Gine',
  'Gine-Bissau',
  'Grenada',
  'Grönland',
  'Guadeloupe',
  'Guam',
  'Guatemala',
  'Guernsey',
  'Guyana',
  'Güney Afrika',
  'Güney Georgia ve Güney Sandwich Adaları',
  'Güney Kore',
  'Güney Sudan',
  'Gürcistan',
  'Haiti',
  'Heard Adası ve McDonald Adaları',
  'Hırvatistan',
  'Hindistan',
  'Hollanda',
  'Honduras',
  'Hong Kong',
  'Irak',
  'İran',
  'İrlanda',
  'İspanya',
  'İsrail',
  'İsveç',
  'İsviçre',
  'İtalya',
  'İzlanda',
  'Jamaika',
  'Japonya',
  'Jersey',
  'Kamboçya',
  'Kamerun',
  'Kanada',
  'Karadağ',
  'Katar',
  'Kazakistan',
  'Kenya',
  'Kıbrıs',
  'Kırgızistan',
  'Kiribati',
  'Kolombiya',
  'Komorlar',
  'Kongo',
  'Kongo Demokratik Cumhuriyeti',
  'Kosova',
  'Kosta Rika',
  'Kuveyt',
  'Kuzey Kore',
  'Kuzey Makedonya',
  'Kuzey Mariana Adaları',
  'Küba',
  'Laos',
  'Lesotho',
  'Letonya',
  'Liberya',
  'Libya',
  'Liechtenstein',
  'Litvanya',
  'Lübnan',
  'Lüksemburg',
  'Macaristan',
  'Madagaskar',
  'Makao',
  'Malavi',
  'Maldivler',
  'Malezya',
  'Mali',
  'Malta',
  'Man Adası',
  'Marshall Adaları',
  'Martinik',
  'Mauritius',
  'Mayotte',
  'Meksika',
  'Mısır',
  'Mikronezya',
  'Moğolistan',
  'Moldova',
  'Monako',
  'Montserrat',
  'Moritanya',
  'Mozambik',
  'Myanmar',
  'Namibya',
  'Nauru',
  'Nepal',
  'Nijer',
  'Nijerya',
  'Nikaragua',
  'Niue',
  'Norfolk Adası',
  'Norveç',
  'Orta Afrika Cumhuriyeti',
  'Özbekistan',
  'Pakistan',
  'Palau',
  'Panama',
  'Papua Yeni Gine',
  'Paraguay',
  'Peru',
  'Pitcairn Adaları',
  'Polonya',
  'Portekiz',
  'Porto Riko',
  'Réunion',
  'Romanya',
  'Ruanda',
  'Rusya',
  'Saint Barthélemy',
  'Saint Helena, Ascension ve Tristan da Cunha',
  'Saint Kitts ve Nevis',
  'Saint Lucia',
  'Saint Martin (Fransız Bölgesi)',
  'Saint Pierre ve Miquelon',
  'Saint Vincent ve Grenadinler',
  'Samoa',
  'San Marino',
  'São Tomé ve Príncipe',
  'Senegal',
  'Seyşeller',
  'Sırbistan',
  'Sierra Leone',
  'Singapur',
  'Sint Maarten (Hollanda Bölgesi)',
  'Slovakya',
  'Slovenya',
  'Solomon Adaları',
  'Somali',
  'Sri Lanka',
  'Sudan',
  'Surinam',
  'Suriye',
  'Suudi Arabistan',
  'Svalbard ve Jan Mayen',
  'Şili',
  'Tacikistan',
  'Tanzanya',
  'Tayland',
  'Tayvan',
  'Timor-Leste',
  'Togo',
  'Tokelau',
  'Tonga',
  'Trinidad ve Tobago',
  'Tunus',
  'Turks ve Caicos Adaları',
  'Tuvalu',
  'Türkiye',
  'Türkmenistan',
  'Uganda',
  'Ukrayna',
  'Umman',
  'Uruguay',
  'Ürdün',
  'Vanuatu',
  'Vatikan',
  'Venezuela',
  'Vietnam',
  'Virjin Adaları (ABD)',
  'Virjin Adaları (Britanya)',
  'Wallis ve Futuna',
  'Yemen',
  'Yeni Kaledonya',
  'Yeni Zelanda',
  'Yunanistan',
  'Zambiya',
  'Zimbabve',
]
